import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Home, CheckCircle2, Users, Target, BookOpen, MapPin } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const directorateData: Record<string, {
  title: string;
  description: string;
  mandate: string;
  departments: {
    name: string;
    functions: string[];
  }[];
  keyActivities: string[];
}> = {
  'animal-resources': {
    title: 'Directorate of Animal Resources',
    description: 'The Directorate of Animal Resources is responsible for the development and management of the livestock sub-sector in Uganda. It oversees animal production, animal health, and entomology services to ensure sustainable livestock development.',
    mandate: 'To promote and regulate the livestock industry for improved animal production, productivity, and trade while controlling animal diseases and ensuring food safety from animal-source foods.',
    departments: [{
      name: 'Department of Animal Production',
      functions: ['Livestock breeding and genetic improvement programs', 'Pasture and range management', 'Livestock marketing and value chain development', 'Apiculture (beekeeping) development', 'Poultry and small stock development']
    }, {
      name: 'Department of Animal Health',
      functions: ['Disease surveillance, prevention, and control', 'Veterinary public health and meat inspection', 'Regulation of veterinary drugs and biologicals', 'Quarantine and border inspection services', 'Tsetse and trypanosomiasis control coordination']
    }, {
      name: 'Department of Entomology',
      functions: ['Tsetse fly surveillance and control', 'Vector-borne disease management', 'Integrated pest management for livestock', 'Research coordination on livestock parasites']
    }],
    keyActivities: ['National livestock vaccination campaigns against FMD, PPR, CBPP, and other notifiable diseases', 'Livestock census and population surveys', 'Regulation of livestock movement and trade', 'Capacity building for district veterinary officers', 'Coordination with NAGRC&DB on breeding programs', 'Meat and dairy safety inspection and certification']
  },
  'crop-resources': {
    title: 'Directorate of Crop Resources',
    description: 'The Directorate of Crop Resources oversees crop production, protection, and regulation in Uganda. It ensures food security through promotion of improved crop varieties, pest and disease management, and quality assurance of agricultural inputs.',
    mandate: 'To promote sustainable crop production and productivity, ensure food security, and regulate the quality of crop inputs and products for domestic consumption and export.',
    departments: [{
      name: 'Department of Crop Production',
      functions: ['Promotion of improved crop varieties and agronomic practices', 'Soil fertility management and conservation agriculture', 'Irrigation and water management for crop production', 'Horticulture development (fruits, vegetables, flowers)', 'Industrial crops promotion (cotton, tea, tobacco)']
    }, {
      name: 'Department of Crop Protection',
      functions: ['Plant pest and disease surveillance and control', 'Phytosanitary inspection and certification', 'Pesticide registration and regulation', 'Quarantine services for plant materials', 'Integrated pest management promotion']
    }, {
      name: 'Department of Crop Inspection and Certification',
      functions: ['Seed certification and quality testing', 'Variety evaluation and release', 'Plant variety protection', 'Regulation of seed trade and distribution']
    }],
    keyActivities: ['National seed certification and distribution programs', 'Fall Armyworm and locust surveillance and response', 'Promotion of climate-resilient crop varieties', 'Phytosanitary certification for agricultural exports', 'Regulation of agrochemical imports and distribution', 'Coordination of the National Crop Variety Release Committee']
  },
  'fisheries-resources': {
    title: 'Directorate of Fisheries Resources',
    description: "The Directorate of Fisheries Resources manages and regulates Uganda's fisheries and aquaculture sub-sector. It ensures sustainable utilization of fisheries resources across Uganda's lakes, rivers, and fish farms.",
    mandate: 'To promote sustainable management and development of fisheries and aquaculture resources for food security, employment, and export earnings.',
    departments: [{
      name: 'Department of Fisheries Management',
      functions: ['Fisheries policy formulation and regulation', 'Licensing of fishing activities and vessels', 'Fish stock assessment and monitoring', 'Control of illegal, unreported, and unregulated (IUU) fishing', 'Community-based fisheries management']
    }, {
      name: 'Department of Aquaculture Development',
      functions: ['Promotion of fish farming and cage culture', 'Hatchery development and fingerling production', 'Fish feed development and quality assurance', 'Training of fish farmers in modern aquaculture', 'Aquaculture park development']
    }, {
      name: 'Department of Fish Quality Assurance',
      functions: ['Inspection of fish processing establishments', 'Fish export certification (EU and international markets)', 'HACCP compliance monitoring', 'Laboratory testing for fish safety and quality']
    }],
    keyActivities: ['Enforcement of fishing regulations on Lakes Victoria, Albert, Kyoga, and Edward', 'Promotion of cage fish farming as alternative to capture fisheries', 'Fish quality inspection for EU market compliance', 'Restocking of depleted water bodies', 'Beach Management Unit (BMU) capacity building', 'Combating illegal fishing methods and gear']
  },
  'extension-services': {
    title: 'Directorate of Agricultural Extension Services',
    description: 'The Directorate of Agricultural Extension Services coordinates the delivery of agricultural advisory and extension services to farmers across Uganda. It bridges the gap between research and farmers through technology transfer and capacity building.',
    mandate: 'To coordinate and facilitate the delivery of demand-driven, pluralistic, and decentralized agricultural extension and advisory services to farmers and agribusinesses.',
    departments: [{
      name: 'Department of Extension Coordination',
      functions: ['Coordination of public and private extension service providers', 'Development of extension methodologies and approaches', 'Monitoring and evaluation of extension service delivery', 'Capacity building for district extension staff', 'Integration of ICT in extension services']
    }, {
      name: 'Department of Farm Development',
      functions: ['Farmer group formation and strengthening', 'Farmer Field School (FFS) methodology promotion', 'Agricultural mechanization advisory', 'Post-harvest handling and storage advisory', 'Agribusiness development support']
    }, {
      name: 'Department of Agricultural Infrastructure',
      functions: ['Water for agricultural production', 'Rural agricultural infrastructure development', 'Irrigation scheme development and management', 'Agricultural market infrastructure']
    }],
    keyActivities: ['Training of district and sub-county extension workers', 'Farmer Field School establishment across all districts', 'Agricultural technology demonstration sites', 'Coordination with NAADS on input distribution', 'Development of extension materials and farmer guides', 'ICT-based extension services (e-extension)']
  }
};
export function DirectoratePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const data = slug ? directorateData[slug] : null;
  if (!data) {
    return <>
        <Header data-id="element-758" />
        <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-759">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 text-center" data-id="element-760">
            <h1 className="text-3xl font-bold text-uganda-black mb-4" data-id="element-761">
              Page Not Found
            </h1>
            <Link to="/" className="text-uganda-green font-semibold hover:underline" data-id="element-762">
              Return to Home
            </Link>
          </div>
        </main>
        <Footer data-id="element-763" />
      </>;
  }
  return <>
      <Header data-id="element-764" />
      <main className="flex-grow pt-[160px] md:pt-[148px] bg-gray-50 pb-20" data-id="element-765">
        <div className="relative text-white py-32 md:py-48 overflow-hidden" data-id="element-766">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=1600&h=600&fit=crop")'
        }} data-id="element-767"></div>
          <div className="absolute inset-0 bg-uganda-green/80" data-id="element-768"></div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-8" data-id="element-769">
            <div className="flex items-center text-sm text-green-100 mb-6" data-id="element-770">
              <Link to="/" className="hover:text-white flex items-center" data-id="element-771">
                <Home className="w-4 h-4 mr-1" data-id="element-772" /> Home
              </Link>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-773" />
              <span data-id="element-774">About MAAIF</span>
              <ChevronRight className="w-4 h-4 mx-2" data-id="element-775" />
              <span className="text-white font-medium" data-id="element-776">{data.title}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-777">
              {data.title}
            </h1>
            <p className="text-lg text-green-50 max-w-3xl leading-relaxed" data-id="element-778">
              {data.description}
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-12" data-id="element-779">
          {/* Mandate */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12" data-id="element-780">
            <div className="flex items-center gap-3 mb-4" data-id="element-781">
              <Target className="w-6 h-6 text-uganda-green" data-id="element-782" />
              <h2 className="text-2xl font-bold text-uganda-black" data-id="element-783">Mandate</h2>
            </div>
            <p className="text-gray-600 leading-relaxed" data-id="element-784">{data.mandate}</p>
          </div>

          {/* Departments */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-785">
            Departments
          </h2>
          <div className="space-y-6 mb-12" data-id="element-786">
            {data.departments.map((dept, index) => <motion.div key={dept.name} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8" data-id="element-787">
                <h3 className="text-xl font-bold text-uganda-black mb-4" data-id="element-788">
                  {dept.name}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-789">
                  {dept.functions.map((func, i) => <div key={i} className="flex items-start gap-3" data-id="element-790">
                      <CheckCircle2 className="w-5 h-5 text-uganda-green flex-shrink-0 mt-0.5" data-id="element-791" />
                      <p className="text-sm text-gray-600" data-id="element-792">{func}</p>
                    </div>)}
                </div>
              </motion.div>)}
          </div>

          {/* Key Activities */}
          <h2 className="text-2xl font-bold text-uganda-black mb-6" data-id="element-793">
            Key Activities
          </h2>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8" data-id="element-794">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-795">
              {data.keyActivities.map((activity, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.05
            }} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50" data-id="element-796">
                  <CheckCircle2 className="w-5 h-5 text-uganda-green flex-shrink-0 mt-0.5" data-id="element-797" />
                  <p className="text-sm text-gray-700" data-id="element-798">{activity}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-799" />
    </>;
}